<template>
  <div class="your-dashboard-component">
    <div class="container">
      <div class="row">
        <div class="col-12">
          <div class="title d-flex flex-row justify-content-between align-items-center">
            <h1>{{ heading }}</h1>
            <a href="#" title="#" @click="$router.push('/my-details-and-documents')">{{ myDetailsAndDocumentsLinkText }}</a>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <h2 class="subtitle">{{ subtitle }}</h2>
        </div>
      </div>
      <div class="row">
        <div class="col-10">
          <div class="body">
            <p>{{ description }}</p>
          </div>
        </div>
        <div class="col-2 d-flex flex-row justify-content-end align-items-end">
          <button @click="prev()" class="prev"></button>
          <button @click="next()" class="next"></button>
        </div>
      </div>
      <div class="row" v-if="replacementQuotesAvailable" style="margin-bottom:30px;">
        <div class="col-md-12">
          <div v-for="(quotes, scheme) in replacementQuotes" class="replacement-notice d-flex justify-content-between align-items-center" style="margin-bottom:10px;">
            <template v-if="orderRequest">
              <span>You've received a replacement {{schemeFullName(scheme)}} quote in place of your ordered vehicle</span>
            </template>
            <template v-else>
              <span>A {{schemeFullName(scheme)}} vehicle quote has been created for you</span>
            </template>
            <router-link :to="{ name: 'Replacement Quotes', params: { scheme: scheme } }">
              View quote details
            </router-link>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12" v-if="loaded">
          <slick
            ref="slick"
            :options="slickOptions"
            @reInit="reInit">
            <div v-if="(!hasActiveOrders && !hasCurrentAllocations) || canReOrder">
              <div v-if="hasMultipleProductTypes" class="card card--multigrade card--order-a-new-car">
                <div style="background:url('/images/order-a-new-car@full.png') center center no-repeat;background-size:cover;width:250px;height:100%;flex-grow:0;flex-shrink:0;flex-basis:250px;"></div>
                <div class="card-content">
                  <h3>{{ orderANewVehicleHeading }}</h3>
                  <p>{{ orderANewVehicleDescription }}</p>
                  <div class="grade-options">
                    <template v-for="(product, key) in driverProfile.driverGrades" :key="key">
                      <div v-if="adminPortalData.content.products[product.id]">
                        <input type="radio" :id="product.id" :value="product.id" v-model="productSelected">
                        <label :for="product.id">
                          <span class="scheme-name">{{ product.name }}</span>
                          <span class="scheme-desc">{{getMultigradeButtonText(product.id)}}</span>
                        </label>
                      </div>
                    </template>
                  </div>
                  <button @click="$router.push({ name: 'Your Profile', params: { scheme: productSelected } })" class="button button--independence-ghost" :class="{ 'button--independence-ghost--disabled': !productSelected }" :disabled="!productSelected">
                    {{ orderANewVehicleButtonText }}
                  </button>
                </div>
              </div>
              <div v-else class="card card--order-a-new-car">
                <img src="/images/order-a-new-car@2x.png" alt="">
                <h3>{{ orderANewVehicleHeading }}</h3>
                <p>{{ orderANewVehicleDescription }}</p>
                <button @click="$router.push({ name: 'Your Profile', params: { scheme: defaultScheme.id } })" class="button button--independence-ghost">
                  {{ orderANewVehicleButtonText }}
                </button>
              </div>
            </div>
            <div v-if="orderRequest" class="card card--ordered-car">
              <div class="d-flex flex-row justify-content-between">
                <span class="type">Order Request Submitted</span>
              </div>
              <vehicle-image :derivative="orderRequest.derivative" :showMain="true"/>
              <h3 class="make"><span>{{ orderRequest.derivative.manufacturerName }}</span>
                {{ orderRequest.derivative.modelName }}</h3>
              <p class="model">{{ orderRequest.derivative.derivativeName }}</p>
              <!-- <p>Order Requested - we are seeking sign off from your employer.</p>  -->
              <p>Quote requested: <b>Awaiting approval</b>. Our team are working on checking your request and we will then seek approval from your employer.</p>
            </div>

            <!-- Allocations -->
            <div v-for="allocation in driverProfile.allocations.current">
              <div class="card card--ordered-car">
                <div class="d-flex flex-row justify-content-between">
                  <span class="type">Current Vehicle</span>
                  <span class="reg">{{allocation.vehicle.registrationNumber}}</span>
                </div>
                <vehicle-image :derivative="allocation.derivative" :showMain="true"/>
                <h3 class="make"><span>{{ allocation.vehicle.manufacturerName }}</span> {{ allocation.vehicle.modelName }}</h3>
                <p class="model">{{ allocation.vehicle.derivativeName }}</p>
                <p>Allocated: <strong>{{ formattedDate(allocation.allocationDate) }}</strong></p>

                <router-link :to="{ name: 'my-vehicle', params: { contractId: allocation.contract.contractID } }" class="button button--independence-ghost text-center">
                  My Vehicle Details
                </router-link>


              </div>
            </div>

            <!-- Orders -->
            <div v-for="order in orders">
              <div class="card card--ordered-car">
                <div class="d-flex flex-row justify-content-between">
                  <span class="type">{{ orderStatus(order.orderStatusDescription) }}</span>
                </div>
                <vehicle-image :derivative="order.derivative" :showMain="true"/>
                <h3 class="make"><span>{{ order.vehicle.manufacturerName }}</span> {{ order.vehicle.modelName }}</h3>
                <p class="model">{{ order.vehicle.derivativeName }}</p>
                <div class="documents-awaiting"
                  v-if="order?.quote?.scheme !== 'bch' && !isSalSacOrder(order) && order.orderStatusDescription == 'Order Accepted'">
                  <span>You have funding documents<br/>awaiting to be signed</span>
                  <router-link :to="{ name: 'Order Status', params: { orderId: order.orderID } }">
                    View
                  </router-link>
                </div>

                <router-link :to="{ name: 'Order Status', params: { orderId: order.orderID } }"
                  class="button button--independence-ghost">
                  {{ orderedVehicleButtonText }}
                </router-link>

                <!-- {{ currentVehicleButtonText }} -->

              </div>
            </div>
          </slick>
        </div>
      </div>
    </div>
    <footer-text v-if="footerText" :footerText="footerText"/>
  </div>
</template>

<script>

import Slick from 'vue-slick';
import VehicleImage from '@/components/vehicle-details/vehicle-image';
import FooterText from "./footer/footer-text";
import {EventBus} from '@/event-bus.js';
import orderStatusHelper from "@/helpers/order-status";

export default {

  components: {FooterText, Slick, VehicleImage},

  data() {
    return {
      productSelected: false,
      slickOptions: {
        slidesToShow: 3,
        dots: false,
        infinite: false,
        cssEase: 'cubic-bezier(0.455, 0.03, 0.515, 0.955)',
        speed: 750,
        variableWidth: true,
        responsive: [
          {
            breakpoint: 1200,
            settings: {
              slidesToShow: 2.25,
            }
          }
        ]
      },
      loadedOrders: [],
    };
  },

  props: [
    'session',
    'adminPortalData',
    'adminPortalPrimaryProduct'
  ],

  async beforeMount(to, from, next) {
    this.vehicles = [];

    EventBus.$emit('fetchData', [
      'driverprofile',
      'drivergrades',
      'profile',
      'orders',
      'vehiclesearch/derivatives',
      'allocations',
    ]);

    this.type = this.$route.meta.type;
  },

  computed: {
    defaultScheme() {
      if (this?.session?.driverProfile?.driverGrades) {
        return this.session.driverProfile.driverGrades[Object.keys(this.session.driverProfile.driverGrades)[0]];
      } else {
        return null;
      }
    },

    canReOrder() {
      return this?.session?.driverProfile?.canReorder;
    },

    orderStatus() {
      return (jaamaStatus) => orderStatusHelper.origoStatusToStatusTitle(orderStatusHelper.key2StatusToOrigoStatus(jaamaStatus, false));
    },

    orderStatusUrl(order) {
      return '/order-status' + order.orderID;
    },

    loaded() {
      return !!(this.session.profile) && !!(this.session.orders) && !!(this.session.driverProfile);
    },

    orderRequest() {
      return this.session?.driverProfile?.orderRequest;
    },

    hasMultipleProductTypes() {
      return Object.keys(this.session?.driverProfile?.driverGrades).length > 1;
    },

    hasActiveOrders() {
      let has = false;
      if (this?.session?.driverProfile?.orderRequestQuoteID) {
        has = true;
      } else if (this.session.orders) {
        this.session.orders.results.forEach((order) => {
          if (order.orderStatusDescription !== 'Cancelled') {
            has = true;
          }
        })
      }
      return has;
    },

    hasCurrentAllocations() {
      return this.session.driverProfile.allocations.current.length;
    },

    replacementQuotes() {
      return this?.session?.driverProfile?.replacementQuotes ?? [];
    },

    replacementQuotesAvailable() {
      return Object.keys(this.replacementQuotes).length > 0;
    },

    driverProfile() {
      return this.session?.driverProfile ?? null;
    },

    driverGrades() {
      return this.session?.driverProfile?.driverGrades ?? [];
    },

    orders() {
      let orders = []
      if (this.session.orders) {
        this.session.orders.results.forEach((order) => {
          if (order.orderStatusDescription !== 'Cancelled') {
            orders.push(order);
          }
        })
      }

      orders.sort((order1, order2) => {
        let order1Status = (order1.orderStatusDescription === 'Implemented' ? 2 : 1);
        let order2Status = (order2.orderStatusDescription === 'Implemented' ? 2 : 1);

        return order1Status - order2Status;
      });

      return orders
    },

    canOrder() {
      return (!this.hasActiveOrders);
    },

    heading() {
      return this.adminPortalPrimaryProduct.dashboard_heading;
    },

    subtitle() {
      return this.adminPortalPrimaryProduct.dashboard_subtitle;
    },

    description() {
      return this.adminPortalPrimaryProduct.dashboard_description;
    },

    myDetailsAndDocumentsLinkText() {
      return this.adminPortalData.content ? this.adminPortalData.content.global.dashboard_my_details_and_documents_link_text : '';
    },

    orderANewVehicleHeading() {
      return this.adminPortalPrimaryProduct.order_a_new_vehicle_heading;
    },

    orderANewVehicleDescription() {
      return this.adminPortalPrimaryProduct.order_a_new_vehicle_description;
    },

    orderANewVehicleButtonText() {
      return this.adminPortalPrimaryProduct.order_a_new_vehicle_button_text;
    },

    orderedVehicleButtonText() {
      return this.adminPortalPrimaryProduct.ordered_vehicle_button_text;
    },

    currentVehicleButtonText() {
      return this.adminPortalPrimaryProduct.current_vehicle_button_text;
    },

    footerText() {
      return this.adminPortalData.content?.products?.ecoamaps
      && this.adminPortalData.content?.products?.ecoamaps[0]?.fca_requirement_and_content_fca_description_required
      && this.adminPortalData.content?.products?.ecoamaps[0]?.fca_requirement_and_content_fca_description
      && this.adminPortalData.content?.products?.ecoamaps[0]?.fca_description_required_on_dashboard
        ? this.adminPortalData.content.products.ecoamaps[0].fca_requirement_and_content_fca_description
        : false;
    }
  },

  methods: {
    schemeFullName(scheme) {
      return this.session.schemeNames[scheme];
    },
    getMultigradeButtonText(grade) {
      return this.adminPortalData.content.products[grade][0].order_a_new_vehicle_multigrade_description;
    },
    hasOrders() {
      return (this.session.orders ? this.session.orders.results.length : false);
    },

    isSalSacOrder(order) {
      return order.contract.productDescription.includes('Salary Sacrifice');
    },

    next() {
      this.$refs.slick.next();
    },

    prev() {
      this.$refs.slick.prev();
    },

    reInit() {
      this.$nextTick(() => {
        this.$refs.slick.reSlick();
      });
    },
    formattedDate(dateString) {
      if (!dateString) {
        return '';
      }

      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, options);
    }
  },
}

</script>
